/**
 * M4-S3 — shared client-side reader for the published vehicle index
 * (system-design.md §4.1). One file, fetched once and shared by every
 * `custom-wiper-finder` instance on the page via a singleton promise, so N
 * finder renders cost one request, not N.
 *
 * `window.WiperFinderIndex` is guarded so that if this asset tag is ever
 * emitted more than once on the same page (theme-contract.md §8.1: the
 * finder snippet renders in up to 9 places), re-running this file is a
 * harmless no-op rather than a redefinition.
 */
if (typeof window.WiperFinderIndex === 'undefined') {
  window.WiperFinderIndex = (function () {
    var SCHEMA_VERSION = 1;
    var TIMEOUT_MS = 8000;
    var pending = null;
    var downloading = null;

    /**
     * Fetches and validates the index. Rejects on network failure, non-200,
     * parse error, timeout, or an unrecognised schema_version — the caller
     * treats any rejection as "fall back to the server" (system-design.md
     * §5). Resolves the *same* promise for every caller while a load is in
     * flight; a later call after a rejection starts a fresh attempt rather
     * than replaying the old failure forever.
     */
    function load(url) {
      if (!url) {
        return Promise.reject(new Error('WiperFinderIndex: no index URL configured'));
      }

      if (pending) {
        return pending;
      }

      pending = new Promise(function (resolve, reject) {
        var timer = setTimeout(function () {
          reject(new Error('WiperFinderIndex: timed out after ' + TIMEOUT_MS + 'ms'));
        }, TIMEOUT_MS);

        download(url).then(
          function (data) {
            clearTimeout(timer);
            resolve(data);
          },
          function (err) {
            clearTimeout(timer);
            reject(err);
          }
        );
      });

      // Don't let a stale rejected promise permanently wedge every later
      // widget instance on the page into fallback mode.
      pending.catch(function () {
        pending = null;
      });

      return pending;
    }

    /**
     * The download itself. The timeout in load() only stops *waiting*: the
     * request is never aborted, so a slow first load (LogRocket 2026-10-21:
     * aborted at 2.999 s, same file 536 ms on the next page) still finishes
     * and is reused by the next load() call instead of being thrown away and
     * logged as a "Network Error 0". Shared across callers and reset on
     * failure so a later call retries.
     */
    function download(url) {
      if (downloading) {
        return downloading;
      }

      downloading = fetch(url)
        .then(function (response) {
          if (!response.ok) {
            throw new Error('WiperFinderIndex: HTTP ' + response.status);
          }
          return response.json();
        })
        .then(function (data) {
          if (!data || data.schema_version !== SCHEMA_VERSION) {
            throw new Error('WiperFinderIndex: unknown schema_version ' + (data && data.schema_version));
          }
          if (!data.tree || !data.vehicles) {
            throw new Error('WiperFinderIndex: missing tree or vehicles');
          }
          return data;
        });

      downloading.catch(function () {
        downloading = null;
      });

      return downloading;
    }

    function makes(index) {
      return Object.keys(index.tree);
    }

    function models(index, make) {
      var node = index.tree[make];
      return node ? Object.keys(node) : [];
    }

    function years(index, make, model) {
      var node = index.tree[make] && index.tree[make][model];
      return node ? Object.keys(node) : [];
    }

    /**
     * The builder's "" sentinel (VehicleIndexBuilder) means "this year has
     * no generation split" — matches the `[""]` shape `/getGenerations`
     * returns in v1 (api-contract.md). Real generation keys and the ""
     * sentinel are not expected to co-exist for one make/model/year; if
     * they ever did, this surfaces only the real ones and treats the leaf
     * as generation-less otherwise.
     */
    function generationsFor(index, make, model, year) {
      var node = index.tree[make] && index.tree[make][model] && index.tree[make][model][year];

      if (!node) {
        return { hasGenerations: false, list: [] };
      }

      var real = Object.keys(node).filter(function (key) {
        return key !== '';
      });

      return { hasGenerations: real.length > 0, list: real };
    }

    /**
     * A leaf can hold more than one ka_code — real duplicate, not a bug:
     * TOYOTA FORTUNER / 2015-2022 / 2nd Generation (NOTES.md, M4-S3). Takes
     * the first of the leaf's already-sorted codes (VehicleIndexBuilder
     * sorts every leaf with SORT_STRING before writing the file), matching
     * WiperController::byMakeModelYear's `orderBy('ka_code')` so the index
     * and server-fallback paths can't disagree about which car a shopper
     * gets. Pending the client's confirmation of which of the two is
     * correct (NOTES.md, M4-S3) — a data fix, not a widget fix.
     */
    function resolveKaCode(index, make, model, year, generation) {
      var genKey = generation || '';
      var node = index.tree[make] && index.tree[make][model] && index.tree[make][model][year];
      var codes = node && node[genKey];

      return codes && codes.length ? codes[0] : null;
    }

    function vehicle(index, kaCode) {
      return index.vehicles[kaCode] || null;
    }

    return {
      load: load,
      makes: makes,
      models: models,
      years: years,
      generationsFor: generationsFor,
      resolveKaCode: resolveKaCode,
      vehicle: vehicle,
    };
  })();
}
